<template>
  <div class="flex w-full relative bg-gray-50 dark:bg-navy-1000">
    <AdministrationSidebar />
    <!-- ACTIVE ADMIN PAGE -->
    <div
      id="administrationScrollable"
      class="flex flex-col w-full bg-gray-50 dark:bg-navy-1000"
    >
      <!-- ADMIN HEADER -->
      <div class="flex-col items-center flex">
        <div
          class="sectionHeader flex items-center justify-center w-full z-20 lg:z-15 lg:transition-all duration-200 transform"
          :class="headerScrolled"
        >
          <!-- ADMINISTRATION LOGO -->
          <AdministrationLogo
            class="transition-all duration-200"
            :class="logoClass"
          />
        </div>
      </div>
      <!-- SELECTED ADMIN COMPONENT -->
      <div v-if="readyToLoad" :class="pageClass">
        <component
          :is="currentComponent"
          v-if="currentComponent"
        />
        <section
          v-else
          class="py-16 px-5 flex flex-col justify-center items-center"
        >
          <div class="animation-container group relative w-32 h-32 flex items-center justify-center rounded-full">
            <div class="key-orbit-wrapper absolute inset-0">
              <div class="key-orbit key-orbit-top">
                <font-awesome-icon
                  icon="fa-solid fa-key-skeleton"
                  class="key-icon text-gray-600 dark:text-primary-50 w-10 h-10"
                />
              </div>

              <div class="key-orbit key-orbit-bottom">
                <font-awesome-icon
                  icon="fa-solid fa-key-skeleton"
                  class="key-icon text-gray-600 dark:text-primary-50 w-10 h-10"
                />
              </div>
            </div>

            <font-awesome-icon
              icon="fa-solid fa-shield-keyhole"
              class="text-primary dark:text-primary-200 w-20 h-20 z-10 transition-transform hover:scale-110 ease-[cubic-bezier(.84,.68,.27,1.42)]"
            />
          </div>
          <div class="mt-6 whitespace-pre-wrap">
            <p>
              These pages unlock admin-only data and configurations.
            </p>
            <p>
              Choose an option from the sidebar to get started.
            </p>
          </div>
        </section>
      </div>
      <div v-else class="w-full h-full">
        <LoadingSpinnerPage />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex"
import { defineAsyncComponent } from "vue"
import AdministrationSidebar from "./AdministrationSidebar.vue"
import LoadingSpinnerPage from "@/components/Loading/LoadingSpinner/LoadingSpinnerPage.vue"
import AdministrationLogo from "@/assets/Administration_Navigation_Icon_Full_Color.svg"
import { getAllEmployees } from "../../Site-Track/services/ManagerService"
import { getImagesBySources } from "@/services/images.service"

const RabbitMQDashboard = defineAsyncComponent({
  loader: () => import("../views/RabbitMQDashboard.vue"),
  loadingComponent: LoadingSpinnerPage,
})

const SiteConfigDashboard = defineAsyncComponent({
  loader: () => import("../views/SiteConfigDashboard.vue"),
  loadingComponent: LoadingSpinnerPage,
})

const DeploymentInfoDashboard = defineAsyncComponent({
  loader: () => import("../views/DeploymentInfoDashboard.vue"),
  loadingComponent: LoadingSpinnerPage,
})

const TenantOverview = defineAsyncComponent({
  loader: () => import("../views/TenantOverview.vue"),
  loadingComponent: LoadingSpinnerPage,
})

const SystemMessages = defineAsyncComponent({
  loader: () => import("../views/SystemMessages.vue"),
  loadingComponent: LoadingSpinnerPage,
})

const AdminLogs = defineAsyncComponent({
  loader: () => import("./AdminLogs/AdminLogs.vue"),
  loadingComponent: LoadingSpinnerPage,
})

export default {
  name: "AdministrationPage",
  components: {
    AdministrationSidebar,
    LoadingSpinnerPage,
    AdministrationLogo
  },
  computed: {
    ...mapState([
      "headers",
      "screen",
      "orientation"
    ]),
    ...mapGetters([
      "showingModuleHeader",
      "atTopOfPage",
      "loadedGlobalValues",
      "getIsTenantSuperuser",
      "getUserManagementAccess",
      "getEmployeesUserId"
    ]),
    currentComponent() {
      const path = this.$route.path
      if (path.includes("RabbitMQ")) return RabbitMQDashboard
      if (path.includes("SiteConfig")) return SiteConfigDashboard
      if (path.includes("DeploymentInfo")) return DeploymentInfoDashboard
      if (path.includes("TenantOverview")) return TenantOverview
      if (path.includes("SystemMessages")) return SystemMessages
      if (path.includes("AdminLogs")) return AdminLogs
      return null
    },
    headerScrolled() {
      if (this.isMobileLandscape && !this.isTablet && !this.screen.xl) return "hidden"
      let className = "md:fixed lg:shadow-md bg-gray-50 dark:bg-navy-1000 lg:bg-white-off "
      if (this.atTopOfPage || !this.screen.lg) {
        className += "h-16 lg:h-24 "
      }
      else className += "shadow-lg h-24 lg:h-16 "
      if (!this.showingModuleHeader && this.screen.lg) className += "-translate-y-full md:translate-y-0 "
      else className += "-translate-y-0 "
      return className
    },
    pageClass() {
      let classes = "flex flex-col relative "
      if (!this.isMobileLandscape || (this.isTablet)) classes += "md:mt-16 lg:mt-24 "
      return classes
    },
    logoClass() {
      if (this.atTopOfPage && this.screen.lg) return "h-14 lg:h-20 pt-3 pb-5 my-0"
      else return "h-14 lg:h-16 pt-3 pb-5" 
    },
    path() {
      return this.$route.path
    },
    needLoad() {
      // MARK IF PATH REQUIRES STATE VALUES PRELOAD
      return {
        "/Administration/AdminLogs": [
          "allEmployeesData",
        ],
      }
    },
    readyToLoad() {
      if (!this.needLoad[this.path]) return true
      for (let needToLoad of this.needLoad[this.path]) {
        if (!this.loadedGlobalValues[needToLoad]) return false
      }
      return true
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    isMobileLandscape() {
      return this.isMobile && this.orientation.landscape
    },
    isTablet() {
      return /ipad|tablet/i.test(window.navigator.userAgent.toLowerCase())
    },
  },
  mounted() {
    this.showModuleHeader(true)
    if (this.getUserManagementAccess || this.getIsTenantSuperuser) {
      this.loadEmployees()
    }
  },
  methods: {
    ...mapActions(["setFullEmployeeData"]),
    ...mapMutations([
      "setSTPage",
      "showModuleHeader",
      "setPrompt",
      "setAllPermissionSets",
      "setAllEmployeeAvatars"
    ]),
    loadEmployees() {
      let employeeAccess = this.getIsTenantSuperuser
        ||
        // will need to replace with new employee endpoint not just for managers
        this.getUserPermissions.site_track?.manager_tools?.employee_management?.main_view?.object?.read_bool
      if (!employeeAccess) {
        this.setFullEmployeeData([])
        return
      }
      getAllEmployees(this.headers)
      .then(response => {
        if (response?.status === 200) {
          this.setFullEmployeeData(response.data.results)
          this.loadAllEmployeeAvatars(response.data.results)
        }
        else {
          console.log(response)
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access employee data.", "closePrompt", null, { header: "Error" }])
          }
        }
      }, (error) => {
        console.log(error)
      })
    },
    loadAllEmployeeAvatars(employees) {
      if (Object.keys(this.getEmployeesUserId).length === 0) return
      let userIds = []
      for (let user of employees) {
        if (user.user_id) userIds.push(user.user_id)
      }
      getImagesBySources(userIds, this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setAllEmployeeAvatars(response.data.results)
        }
        else console.log(response)
      }).catch(error => console.log(error))
    },
  },
}
</script>

<style scoped>
/* 1. The wrapper handles the fast acceleration, paused by default */
.key-orbit-wrapper {
  animation: orbit .8s linear infinite;
  animation-play-state: paused;
}

/* 2. When the main container (.animation-container) is hovered, spin the wrapper */
.animation-container:hover .key-orbit-wrapper {
  animation-play-state: running;
}

/* 3. Your baseline slow orbit (constantly running) */
.key-orbit {
  position: absolute;
  inset: 0;
  animation: orbit 7s linear infinite;
}

.key-orbit-top {
  transform: rotate(45deg);
}

.key-orbit-bottom {
  transform: rotate(225deg);
}

.key-icon {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
}

@keyframes orbit {
  from {
    rotate: 360deg;
  }
  to {
    rotate: 0deg;
  }
}
</style>
